import { GAME, MAP } from '../utils/constants';
import { mulberry32 } from '../utils/rng';

export type RoundSpawn = {
  extractionX: number;
  extractionZ: number;
  driverX: number;
  driverZ: number;
  driverHeading: number;
  cop1X: number;
  cop1Z: number;
  cop1Heading: number;
  cop2X: number;
  cop2Z: number;
  cop2Heading: number;
};

function roadCoord(i: number, half: number, rw: number, blockW: number): number {
  const roadWithSidewalk = rw + MAP.SIDEWALK_WIDTH * 2;
  return -half + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
}

function fixedFallback(): RoundSpawn {
  return {
    extractionX: GAME.EXTRACTION_X,
    extractionZ: GAME.EXTRACTION_Z,
    driverX: 0,
    driverZ: MAP.HALF * 0.105,
    driverHeading: Math.PI,
    cop1X: GAME.COP_SPAWN_X,
    cop1Z: GAME.COP_SPAWN_Z,
    cop1Heading: Math.PI,
    cop2X: GAME.COP2_SPAWN_X,
    cop2Z: GAME.COP2_SPAWN_Z,
    cop2Heading: Math.PI,
  };
}

/**
 * Picks extraction + driver + two cops on grid road intersections (single-player).
 * Falls back to `GAME` defaults if a good layout is not found quickly.
 */
export function pickRoundSpawn(seed: number): RoundSpawn {
  const rng = mulberry32(seed);
  const { HALF, GRID, ROAD_WIDTH: rw, SIDEWALK_WIDTH: sw, SIZE } = MAP;
  const roadWithSidewalk = rw + sw * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const edge = 2;
  const span = GRID - edge * 2 + 1;

  const pickRC = () => ({
    r: edge + Math.floor(rng() * span),
    c: edge + Math.floor(rng() * span),
  });

  const toXZ = (r: number, c: number) => ({
    x: roadCoord(c, HALF, rw, blockW),
    z: roadCoord(r, HALF, rw, blockW),
  });

  let ext = toXZ(8, 8);
  let driver = toXZ(4, 4);
  let dist = 0;

  for (let t = 0; t < 90; t++) {
    const ex = pickRC();
    const dr = pickRC();
    ext = toXZ(ex.r, ex.c);
    driver = toXZ(dr.r, dr.c);
    dist = Math.hypot(ext.x - driver.x, ext.z - driver.z);
    if (dist > 450 && dist < 920) break;
  }

  if (dist <= 450 || dist >= 920) {
    return fixedFallback();
  }

  const driverHeading = Math.atan2(ext.x - driver.x, ext.z - driver.z);

  const pickCop = (): { x: number; z: number; heading: number } => {
    for (let t = 0; t < 70; t++) {
      const rc = pickRC();
      const p = toXZ(rc.r, rc.c);
      const dDriver = Math.hypot(p.x - driver.x, p.z - driver.z);
      const dExt = Math.hypot(p.x - ext.x, p.z - ext.z);
      if (dDriver > 220 && dDriver < 520 && dExt > 55) {
        return {
          x: p.x,
          z: p.z,
          heading: Math.atan2(driver.x - p.x, driver.z - p.z),
        };
      }
    }
    const fb = toXZ(
      Math.min(GRID - edge, Math.max(edge, 8 + 3)),
      Math.min(GRID - edge, Math.max(edge, 8 - 3)),
    );
    return {
      x: fb.x,
      z: fb.z,
      heading: Math.atan2(driver.x - fb.x, driver.z - fb.z),
    };
  };

  const cop1 = pickCop();
  let cop2 = pickCop();
  let guard = 0;
  while (
    Math.hypot(cop2.x - cop1.x, cop2.z - cop1.z) < 95 &&
    guard++ < 25
  ) {
    cop2 = pickCop();
  }

  return {
    extractionX: ext.x,
    extractionZ: ext.z,
    driverX: driver.x,
    driverZ: driver.z,
    driverHeading,
    cop1X: cop1.x,
    cop1Z: cop1.z,
    cop1Heading: cop1.heading,
    cop2X: cop2.x,
    cop2Z: cop2.z,
    cop2Heading: cop2.heading,
  };
}
